import Box, { type BoxProps } from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip.tsx';
import { segmentExpiredText, segmentSoonWillBeExpired, segmentStatus } from '@openx/types/segments.ts';
import {
  type Audience,
  AudienceStatus,
  audienceExpiredText,
  audienceSoonWillBeExpiredText,
} from '@openx/types/targeting/audience.ts';
import { isSoonToDate } from '@openx/utils/lib/date.ts';
import { WarningIcon } from '@phosphor-icons/react/Warning';
import { WarningCircleIcon } from '@phosphor-icons/react/WarningCircle';

const StyledTitleBox = (props: BoxProps) => <Box sx={{ whiteSpace: 'pre-line' }} {...props} />;

interface Props {
  audience?: Audience | null;
  fromDeal?: boolean;
  sx?: SxProps<Theme>;
}

export const AudienceExpirationAlerts = ({ audience, fromDeal = false, ...restProps }: Props) => {
  const { status, estimated_expiry_date: estimatedExpiryDate, audience_segments = [] } = audience ?? {};
  const segments = audience_segments.map(({ segment }) => segment);

  const showExpiredAudienceAlert = status === AudienceStatus.EXPIRED;
  const showAudienceExpireSoonAlert = !showExpiredAudienceAlert && isSoonToDate(estimatedExpiryDate);

  const showExpiredSegmentAlert = segments.some(segment => segment.status === segmentStatus.EXPIRED);
  const showSegmentExpireSoonAlert = segments.some(
    segment => status !== segmentStatus.EXPIRED && isSoonToDate(segment.expiration_date)
  );

  const showExpiredAlert = showExpiredSegmentAlert || showExpiredAudienceAlert;
  const showExpireSoonAlert = showSegmentExpireSoonAlert || showAudienceExpireSoonAlert;

  const expiredText = `${showExpiredAudienceAlert ? audienceExpiredText : ''} \n\n${
    showExpiredSegmentAlert ? segmentExpiredText(fromDeal) : ''
  }`.trim();

  const soonWillBeExpiredText = `${showAudienceExpireSoonAlert ? audienceSoonWillBeExpiredText : ''} \n\n${
    showSegmentExpireSoonAlert ? segmentSoonWillBeExpired(fromDeal) : ''
  }`.trim();

  if (!showExpiredAlert && !showExpireSoonAlert) {
    return null;
  }

  return (
    <>
      {showExpiredAlert && (
        <AlertTooltip
          color={theme => theme.palette.error.main}
          title={<StyledTitleBox>{expiredText}</StyledTitleBox>}
          {...restProps}
          icon={<WarningCircleIcon data-test="tooltip-alert-icon" size={20} />}
        />
      )}
      {showExpireSoonAlert && (
        <AlertTooltip
          color={theme => theme.palette.warning.main}
          title={<StyledTitleBox>{soonWillBeExpiredText}</StyledTitleBox>}
          {...restProps}
          icon={<WarningIcon data-test="tooltip-alert-icon" size={20} />}
        />
      )}
    </>
  );
};
